import { useEffect, useMemo, useRef, useState } from 'react';
import { bulk as Api } from '@tvx/api-client';
import { Modal } from './Modal';

/**
 * Tick some rows, do one thing to all of them.
 *
 * Every list screen in the console wants this, so it is one component and one
 * hook rather than thirteen variations that would each grow their own quirks.
 * Two rules it enforces on every screen it appears on:
 *
 * Selection is by id and always intersected with the rows on screen, so a
 * count can never promise an action over rows a filter has hidden or a reload
 * has removed.
 *
 * Deleting always asks first, names what will go, and points at the reversible
 * action next to it. Everything else applies straight away — those are all
 * changes the same bar can undo.
 */

export type Picked = { id: number; label: string };

export type Selection = {
  picked: { id: number }[];
  allOn: boolean;
  isOn: (id: number) => boolean;
  toggle: (id: number, on: boolean) => void;
  toggleMany: (ids: number[], on: boolean) => void;
  toggleAll: (on: boolean) => void;
  clear: () => void;
};

export function useSelection<T extends { id: number }>(rows: T[]) {
  const [sel, setSel] = useState<ReadonlySet<number>>(new Set());
  const picked = useMemo(() => rows.filter((r) => sel.has(r.id)), [rows, sel]);
  const toggleMany = (ids: number[], on: boolean) => setSel((prev) => {
    const next = new Set(prev);
    for (const id of ids) { if (on) next.add(id); else next.delete(id); }
    return next;
  });
  return {
    sel,
    picked,
    allOn: rows.length > 0 && picked.length === rows.length,
    isOn: (id: number) => sel.has(id),
    toggle: (id: number, on: boolean) => toggleMany([id], on),
    toggleMany,
    toggleAll: (on: boolean) => toggleMany(rows.map((r) => r.id), on),
    clear: () => setSel(new Set()),
  };
}

/** Indeterminate is a property, not an attribute — React cannot set it in JSX. */
export function TickAll({ on, some, onChange }: {
  on: boolean; some: boolean; onChange: (v: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = some && !on; }, [some, on]);
  return <th style={{ width: 34 }}>
    <input ref={ref} type="checkbox" checked={on} aria-label="Select all"
      onChange={(e) => onChange(e.target.checked)} />
  </th>;
}

export function Tick({ on, label, onChange }: {
  on: boolean; label: string; onChange: (v: boolean) => void;
}) {
  return <td>
    <input type="checkbox" checked={on} aria-label={`Select ${label}`}
      onChange={(e) => onChange(e.target.checked)} />
  </td>;
}

export function BulkActions({ path, noun, rows, actions, deletable = true, onDone }: {
  /** The console API family, e.g. "/articles". `${path}/bulk` is the endpoint. */
  path: string;
  noun: string;
  rows: Picked[];
  actions: [action: string, label: string][];
  deletable?: boolean;
  onDone: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const n = rows.length;
  /* The last action is the gentle one on every screen here: unpublish, close,
     suspend. Naming it in the delete dialog is the difference between a desk
     that reaches for delete and one that does not. */
  const reversible = actions.at(-1)?.[1] ?? '';

  /* A selection that empties while a dialog is open would otherwise leave a
     "Delete 0" button sitting there. */
  useEffect(() => { if (!n) { setConfirming(false); setError(''); } }, [n]);

  if (!n) return null;

  async function run(action: string) {
    setError('');
    setBusy(action);
    try {
      await Api.bulk(path, action, rows.map((r) => r.id));
      setConfirming(false);
      await onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('');
    }
  }

  return (
    <>
      {error ? <div className="err">{error}</div> : null}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 14,
        padding: '10px 13px', borderRadius: 11, background: 'rgba(var(--goldrgb), .08)',
        border: '1px solid rgba(var(--goldrgb), .22)',
      }}>
        <strong style={{ fontSize: 13 }}>{n} selected</strong>
        <span style={{ flex: 1 }} />
        {actions.map(([action, label]) => (
          <button key={action} className="b bo" type="button" disabled={!!busy}
            onClick={() => void run(action)}>{busy === action ? '…' : label}</button>
        ))}
        {deletable ? (
          <button className="b bo" type="button" disabled={!!busy}
            style={{ color: 'var(--down)', borderColor: 'rgba(240,100,91,.4)' }}
            onClick={() => setConfirming(true)}>Delete</button>
        ) : null}
        <button className="b bo" type="button" onClick={() => void onDone()}>Clear</button>
      </div>

      {confirming ? (
        <Modal
          title={`Delete ${n} ${noun}${n === 1 ? '' : 's'}?`}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <button className="b bo" type="button" onClick={() => setConfirming(false)}>Cancel</button>
              <button className="b bg" type="button" disabled={!!busy}
                style={{ background: 'var(--down)', color: '#fff' }}
                onClick={() => void run('delete')}>
                {busy === 'delete' ? 'Deleting…' : `Delete ${n}`}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            This cannot be undone.
            {reversible ? <> If you only want to take it out of use, <strong>{reversible}</strong>{' '}
              instead — that keeps the record and can be reversed.</> : null}
          </p>
          <ul style={{ margin: '12px 0 0 18px', fontSize: 12.5, color: 'var(--tx2)', lineHeight: 1.7 }}>
            {rows.slice(0, 10).map((r) => <li key={r.id}>{r.label || `#${r.id}`}</li>)}
            {n > 10 ? <li>and {n - 10} more</li> : null}
          </ul>
        </Modal>
      ) : null}
    </>
  );
}
